// src/services/payment/paymentReconciliationService.ts
//
// Service fuer den Zahlungsabgleich-Workflow:
//   - uploadCamtZip: laedt eine CAMT-ZIP-Datei hoch, triggert serverseitigen
//     Auto-Match (siehe netlify/functions/import-camt-zip.js)
//   - markInvoicePaidManually: setzt bezahlt_am/betrag manuell auf einer Rechnung,
//     ohne Bank-Bezug
//   - markInvoiceUnpaid: setzt eine versehentlich als bezahlt markierte
//     Rechnung wieder zurueck auf "offen"
//   - getOpenInvoices / getPaidInvoices: Listen-Loader fuer die UI
//   - getRecentImportRuns: Audit-Trail der Bank-Imports

import { supabase } from '../../shared/services/supabase';
import { netlifyFetch } from '../../shared/services/netlifyFetch';
import { paymentReminderService } from './paymentReminderService';

// ============================================
// TYPES
// ============================================

export interface ImportCamtResult {
  success: boolean;
  error?: string;
  filename?: string;
  total_entries?: number;
  crdt_entries?: number;
  auto_matched_count?: number;
  period_from?: string | null;
  period_to?: string | null;
  matched_invoices?: Array<{
    rechnungsnummer: string;
    betrag: number;
    bookgDt: string;
  }>;
  parse_errors?: Array<{ file: string; error: string }>;
}

export interface OpenInvoiceListItem {
  id: string;
  rechnungsnummer: string;
  rechnungsdatum: string | null;
  brutto_betrag: number;
  empfaenger_firma: string;
  status: 'freigegeben' | 'versendet';
  // FELDER (Feature Zahlungserinnerung): Datum + Stufe der letzten versendeten
  // Mahnung fuer diese Rechnung. NULL = noch nie gemahnt.
  // level: 1 = Zahlungserinnerung, 3 = SEPA-Mahnung, 2 = Letzte Erinnerung
  last_reminder_sent_at: string | null;
  last_reminder_level: number | null;
  // FELD (Feature SEPA-Mahnung): Zahlungsart des Kunden. Steuert, ob der
  // SEPA-Mahnung-Button angeboten wird (nicht bei bereits per Lastschrift
  // zahlenden Kunden). NULL = unbekannt/keiner der Sonderfaelle.
  zahlungsart: string | null;
}

export interface PaidInvoiceListItem {
  id: string;
  rechnungsnummer: string;
  rechnungsdatum: string | null;
  brutto_betrag: number;
  empfaenger_firma: string;
  status: 'freigegeben' | 'versendet';
  bezahlt_am: string;
  bezahlt_betrag: number;
  bezahlt_bank_ref: string | null;
}

export interface BankImportRun {
  id: string;
  imported_at: string;
  imported_by_email: string;
  filename: string;
  format: string;
  total_entries: number;
  crdt_entries: number;
  auto_matched_count: number;
  period_from: string | null;
  period_to: string | null;
}

// ============================================
// SERVICE
// ============================================

export const paymentReconciliationService = {

  /**
   * Liest eine ZIP-Datei (CAMT.052-XMLs) als Base64 und ruft die Netlify-Function
   * import-camt-zip auf. Diese parst alle XMLs, fuehrt Auto-Match aus und
   * schreibt bezahlt_am/betrag/bank_ref auf zugeordnete Rechnungen.
   */
  uploadCamtZip: async (
    file: File,
    importedByEmail: string
  ): Promise<ImportCamtResult> => {
    try {
      // File → ArrayBuffer → Base64
      const arrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      const chunkSize = 0x8000;
      for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(i, i + chunkSize);
        binary += String.fromCharCode.apply(null, Array.from(chunk));
      }
      const base64 = btoa(binary);

      const response = await netlifyFetch('/.netlify/functions/import-camt-zip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          zip_base64: base64,
          filename: file.name,
          imported_by_email: importedByEmail
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        return {
          success: false,
          error: result.error || 'Import fehlgeschlagen'
        };
      }

      return result as ImportCamtResult;
    } catch (error) {
      console.error('[PaymentReconciliation] Fehler beim Upload:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  },

  /**
   * Markiert eine Rechnung manuell als bezahlt.
   * - bezahlt_bank_ref wird NULL gesetzt (keine Bankbuchung verknuepft)
   * - Optimistic Lock via .is('bezahlt_am', null) verhindert Doppel-Markierung
   *
   * @param invoiceId      UUID der Rechnung
   * @param bezahltAm      Datum im Format YYYY-MM-DD
   * @param bezahltBetrag  Betrag in EUR
   */
  markInvoicePaidManually: async (
    invoiceId: string,
    bezahltAm: string,
    bezahltBetrag: number
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .update({
          bezahlt_am: bezahltAm,
          bezahlt_betrag: bezahltBetrag,
          bezahlt_bank_ref: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', invoiceId)
        .is('bezahlt_am', null)
        .select();

      if (error) {
        console.error('[PaymentReconciliation] Fehler beim manuellen Markieren:', error);
        return { success: false, error: error.message };
      }

      if (!data || data.length === 0) {
        return { success: false, error: 'Rechnung wurde nicht aktualisiert (bereits als bezahlt markiert oder nicht gefunden)' };
      }

      return { success: true };
    } catch (error) {
      console.error('[PaymentReconciliation] Unerwarteter Fehler:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  },

  /**
   * Setzt eine als bezahlt markierte Rechnung zurueck auf "offen".
   * Entfernt alle drei Zahlungsspalten. Ermoeglicht Korrektur einer
   * versehentlich automatisch zugeordneten Zahlung.
   */
  markInvoiceUnpaid: async (
    invoiceId: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .update({
          bezahlt_am: null,
          bezahlt_betrag: null,
          bezahlt_bank_ref: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', invoiceId)
        .select();

      if (error) {
        console.error('[PaymentReconciliation] Fehler beim Zuruecksetzen:', error);
        return { success: false, error: error.message };
      }

      if (!data || data.length === 0) {
        return { success: false, error: 'Rechnung nicht gefunden' };
      }

      return { success: true };
    } catch (error) {
      console.error('[PaymentReconciliation] Unerwarteter Fehler:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  },

  /**
   * Laedt alle offenen Rechnungen (freigegeben oder versendet, bezahlt_am IS NULL,
   * nicht-Storno, nicht-importiert). Importierte Bestandsrechnungen sind durch
   * den Backfill in Stufe 1 bereits als bezahlt markiert und tauchen daher
   * gar nicht erst auf.
   *
   * ERWEITERT (Feature Zahlungserinnerung): Reichert pro Rechnung das Datum und
   * die Mahnstufe der letzten versendeten Mahnung an (last_reminder_sent_at,
   * last_reminder_level). Wird in einem separaten Query geladen und client-seitig
   * gemerged.
   *
   * ERWEITERT (Feature SEPA-Mahnung): Reichert zusaetzlich die Zahlungsart des
   * Kunden an (zahlungsart), damit der SEPA-Mahnung-Button in der UI nicht bei
   * bereits per Lastschrift zahlenden Kunden erscheint. Ebenfalls separater
   * Query mit client-seitigem Merge (keine PostgREST-Embeds).
   */
  getOpenInvoices: async (): Promise<OpenInvoiceListItem[]> => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('id, customer_id, rechnungsnummer, rechnungsdatum, brutto_betrag, empfaenger_firma, status')
        .is('bezahlt_am', null)
        .in('status', ['freigegeben', 'versendet'])
        .eq('ist_storno', false)
        .not('rechnungsnummer', 'is', null)
        .order('rechnungsdatum', { ascending: true });

      if (error) {
        console.error('[PaymentReconciliation] Fehler beim Laden offener Rechnungen:', error);
        return [];
      }

      const rows = (data || []) as Array<{
        id: string;
        customer_id: string | null;
        rechnungsnummer: string;
        rechnungsdatum: string | null;
        brutto_betrag: number | string;
        empfaenger_firma: string;
        status: 'freigegeben' | 'versendet';
      }>;

      // "Letzte Mahnung pro Rechnung" separat laden und mergen
      const invoiceIds = rows.map(r => r.id);
      const reminderMap = await paymentReminderService.getLastReminderForInvoices(invoiceIds);

      // Zahlungsart der Kunden separat laden und mergen
      const customerIds = Array.from(new Set(rows.map(r => r.customer_id).filter((v): v is string => !!v)));
      const zahlungsartByCustomer = new Map<string, string | null>();
      if (customerIds.length > 0) {
        const { data: custData, error: custError } = await supabase
          .from('customers')
          .select('id, zahlungsart')
          .in('id', customerIds)
          .limit(100000);
        if (custError) {
          console.error('[PaymentReconciliation] Fehler beim Laden der Kunden-Zahlungsart:', custError);
        } else {
          for (const c of (custData || []) as Array<{ id: string; zahlungsart: string | null }>) {
            zahlungsartByCustomer.set(c.id, c.zahlungsart);
          }
        }
      }

      return rows.map(d => {
        const lastReminder = reminderMap.get(d.id);
        return {
          id: d.id,
          rechnungsnummer: d.rechnungsnummer,
          rechnungsdatum: d.rechnungsdatum,
          brutto_betrag: Number(d.brutto_betrag),
          empfaenger_firma: d.empfaenger_firma,
          status: d.status,
          last_reminder_sent_at: lastReminder?.sent_at || null,
          last_reminder_level: lastReminder?.level || null,
          zahlungsart: d.customer_id ? (zahlungsartByCustomer.get(d.customer_id) || null) : null
        };
      });
    } catch (error) {
      console.error('[PaymentReconciliation] Unerwarteter Fehler:', error);
      return [];
    }
  },

  /**
   * Laedt alle bezahlten Rechnungen (bezahlt_am IS NOT NULL), aber OHNE
   * die importierten Bestandsrechnungen. Letztere sind aus der alten Welt
   * und sollen im Zahlungsabgleich nicht erscheinen.
   */
  getPaidInvoices: async (): Promise<PaidInvoiceListItem[]> => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('id, rechnungsnummer, rechnungsdatum, brutto_betrag, empfaenger_firma, status, bezahlt_am, bezahlt_betrag, bezahlt_bank_ref')
        .not('bezahlt_am', 'is', null)
        .neq('status', 'importiert')
        .not('rechnungsnummer', 'is', null)
        .order('bezahlt_am', { ascending: false });

      if (error) {
        console.error('[PaymentReconciliation] Fehler beim Laden bezahlter Rechnungen:', error);
        return [];
      }

      return (data || []).map((d: {
        id: string;
        rechnungsnummer: string;
        rechnungsdatum: string | null;
        brutto_betrag: number | string;
        empfaenger_firma: string;
        status: 'freigegeben' | 'versendet';
        bezahlt_am: string;
        bezahlt_betrag: number | string;
        bezahlt_bank_ref: string | null;
      }) => ({
        id: d.id,
        rechnungsnummer: d.rechnungsnummer,
        rechnungsdatum: d.rechnungsdatum,
        brutto_betrag: Number(d.brutto_betrag),
        empfaenger_firma: d.empfaenger_firma,
        status: d.status,
        bezahlt_am: d.bezahlt_am,
        bezahlt_betrag: Number(d.bezahlt_betrag),
        bezahlt_bank_ref: d.bezahlt_bank_ref
      }));
    } catch (error) {
      console.error('[PaymentReconciliation] Unerwarteter Fehler:', error);
      return [];
    }
  },

  /**
   * Laedt die N letzten Bank-Import-Runs fuer die Historie-Anzeige.
   */
  getRecentImportRuns: async (limit: number = 5): Promise<BankImportRun[]> => {
    try {
      const { data, error } = await supabase
        .from('bank_import_runs')
        .select('*')
        .order('imported_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('[PaymentReconciliation] Fehler beim Laden der Import-Historie:', error);
        return [];
      }
      return (data || []) as BankImportRun[];
    } catch (error) {
      console.error('[PaymentReconciliation] Unerwarteter Fehler:', error);
      return [];
    }
  }
};
